import { inArray } from "drizzle-orm";
import type { DbClient } from "@/server/platform/db/client";
import { shopSettings } from "@/server/platform/db/schema/chat-tools";
import { nowDate } from "@/shared/lib/date-time";
import type { DbTransaction } from "@/shared/types";

const IMPORT_SETTING_KEYS = [
  "import_default_exchange_rate",
  "import_last_exchange_rate",
] as const;

export type ImportOrderExchangeSettingsDTO = {
  defaultExchangeRate: number;
  lastExchangeRate: number | null;
};

export const DEFAULT_IMPORT_EXCHANGE_RATE = 3500;

export async function getImportOrderExchangeSettings(
  client: DbTransaction | DbClient,
): Promise<ImportOrderExchangeSettingsDTO> {
  const rows = await client
    .select()
    .from(shopSettings)
    .where(inArray(shopSettings.key, [...IMPORT_SETTING_KEYS]));

  const map = new Map(rows.map((row) => [row.key, row.value]));

  return {
    defaultExchangeRate: parsePositiveNumber(
      map.get("import_default_exchange_rate"),
      DEFAULT_IMPORT_EXCHANGE_RATE,
    ),
    lastExchangeRate: parseOptionalPositiveNumber(
      map.get("import_last_exchange_rate"),
    ),
  };
}

export function resolveFormExchangeRate(
  settings: ImportOrderExchangeSettingsDTO,
): number {
  return settings.lastExchangeRate ?? settings.defaultExchangeRate;
}

export async function updateImportDefaultExchangeRate(
  client: DbTransaction,
  defaultExchangeRate: number,
): Promise<ImportOrderExchangeSettingsDTO> {
  const now = nowDate();
  await client
    .insert(shopSettings)
    .values({
      key: "import_default_exchange_rate",
      value: String(defaultExchangeRate),
      updatedAt: now,
    })
    .onConflictDoUpdate({
      target: shopSettings.key,
      set: { value: String(defaultExchangeRate), updatedAt: now },
    });

  return getImportOrderExchangeSettings(client);
}

export async function saveImportLastExchangeRate(
  client: DbTransaction | DbClient,
  exchangeRate: number,
): Promise<void> {
  if (!Number.isFinite(exchangeRate) || exchangeRate <= 0) return;

  const now = nowDate();
  await client
    .insert(shopSettings)
    .values({
      key: "import_last_exchange_rate",
      value: String(exchangeRate),
      updatedAt: now,
    })
    .onConflictDoUpdate({
      target: shopSettings.key,
      set: { value: String(exchangeRate), updatedAt: now },
    });
}

function parsePositiveNumber(raw: string | undefined, fallback: number): number {
  if (!raw) return fallback;
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

function parseOptionalPositiveNumber(raw: string | undefined): number | null {
  if (!raw) return null;
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}
